// data.js
import { convexHull, polyToPoints, pointsToPolyArray, polyAreaAbs, polyBounds } from "./geometry.js";

function maybeDenormCoords(coords, BASE_W, BASE_H) {
  if (!Array.isArray(coords) || coords.length < 6) return null;
  const nums = coords.map(Number);
  const mx = Math.max(...nums.map(v => Math.abs(v)));
  if (mx <= 2.5) {
    // normalized -> base units
    return nums.map((v, i) => v * (i % 2 === 0 ? BASE_W : BASE_H));
  }
  return nums;
}

export async function loadViewerData(url = "../data.json", raw) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
  const RAW = await res.json();

  RAW.rootpath ||= "../archive/";
  RAW.path ||= "./";

  const page = RAW?.page || {};
  const BASE_W = Number(page.width || 0);
  const BASE_H = Number(page.height || 0);
  const TILE_SIZE = Number(page.resWidth || 512);
  const URL_TEMPLATE = RAW.rootpath+RAW.path+page.resUrlTemplate || "./img/output-{z}-{s}-{y}-{x}.jpg";

  const zoomLayers = Array.isArray(page.zoomLayers) ? page.zoomLayers : [];
  const MAX_Z = zoomLayers.length
    ? Math.max(...zoomLayers.filter(z => z?.enable !== false).map(z => Number(z.id || 0)))
    : 0;

  const TITLE =
    RAW?.lngs?.de?.title ||
    RAW?.lngs?.at?.title ||
    RAW?.id ||
    "PIXI Tile Viewer";

  const viewCfg = RAW?.view || {};
  const startZoom = Number(viewCfg?.zoom?.start ?? 1.0);
  const minZoomRaw = Number(viewCfg?.zoom?.minimum);
  const maxZoomRaw = Number(viewCfg?.zoom?.maximum);
  const minZoom = Number.isFinite(minZoomRaw) ? minZoomRaw : undefined;
  const maxZoom = Number.isFinite(maxZoomRaw) ? maxZoomRaw : undefined;
  const startOffX = Number(viewCfg?.offset?.x ?? 0);
  const startOffY = Number(viewCfg?.offset?.y ?? 0);

  // overlays (grouped)
  const overlays = [];
  const overlayMap = {};
  if (!raw) {
    const infoLayers = Array.isArray(page.infoLayers) ? page.infoLayers : [];
    for (let ilIdx = 0; ilIdx < infoLayers.length; ilIdx++) {
      const il = infoLayers[ilIdx] || {};
      const ilMasking = !!(il.masking || il?.render?.masking);
      const ovs = Array.isArray(il.overlays) ? il.overlays : [];
      for (const ov of ovs) {
        const render = ov?.render || {};
        const geometries = Array.isArray(render.geometries) ? render.geometries : [];
        const polys = [];
        for (const g of geometries) {
          if ((g?.shape || "").toLowerCase() !== "polygon") continue;
          const p = maybeDenormCoords(g.coords, BASE_W, BASE_H);
          if (p) polys.push(p);
        }
        if (!polys.length) continue;
        ov.polys=polys;
        overlayMap[ov.id]=ov;
        const hasOvs = Array.isArray(ov.target?.ovs) && ov.target.ovs.length > 0;
        overlays.push({
          id: ov?.id,
          name: ov?.name || ov?.id || "overlay",
          infoLayerIndex: ilIdx,
          infoLayerId: il?.id,
          masking: !!(render.masking || ilMasking || page?.render?.masking),
          zoom: hasOvs?1:ov.target?.zoom,
          target: ov.target,
          actions: ov.actions,
          border: render.border,
          highlightBorder: render.highlightBorder,
          polys,
          order: overlays.length,
        });
      }
    }
  }
  // Precompute bounds & areas + hull
  for (const ov of overlays) {
    ov.bounds = ov.polys.map(polyBounds);
    ov.areas = ov.polys.map(polyAreaAbs);
    const allPts = [];
    for (const poly of ov.polys) allPts.push(...polyToPoints(poly));
    const hullPts = convexHull(allPts);
    ov.hull = pointsToPolyArray(hullPts);
  }

  const mediaMap = {};
  const media = RAW?.media || [];
  
  for (let iMIdx = 0; iMIdx < media.length; iMIdx++) {
    const medium = media[iMIdx] || {};
    mediaMap[medium.id]=medium;
  }
  
  

  
  return {
    RAW,
    page,
    BASE_W, BASE_H,
    TILE_SIZE,
    URL_TEMPLATE,
    MAX_Z,
    TITLE,
    view: { startZoom, minZoom, maxZoom, startOffX, startOffY },
    overlays,
    overlayMap,
    mediaMap,
  };
}
